// CustomerSearchDialog.tsx
import {
  Person as PersonIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import {
  CustomerModel,
  useLazySearchCustomerQuery,
} from "../../store/services/customerService";
import {
  UpdateNextActionRequest,
  useUpdateNextActionMutation,
} from "../../store/services/nextActionService";
import { WorkItemTypeEnum } from "../../store/services/timelineApiRTK";
import { CalendarItem } from "../calendar/calendarModel";

interface CustomerSearchDialogProps {
  open: boolean;
  onClose: () => void;
  calendarEvent: CalendarItem;
}

// Custom hook för debounce
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const CalendarCustomerSearchDialog: React.FC<CustomerSearchDialogProps> = ({
  open,
  onClose,
  calendarEvent,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerModel | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 700);

  const [searchCustomer, { data, isFetching, error }] =
    useLazySearchCustomerQuery();
  const customers = data?.CustomerReturnModel || [];
  const [updateNextAction, { isLoading: isUpdating, error: updateError }] =
    useUpdateNextActionMutation();
  // Reset state when dialog opens/closes
  useEffect(() => {
    const data = calendarEvent.relatedItems?.calendarEvent;
    if (open) {
      setSearchTerm(data?.organizer?.emailAddress.address || "");
      setSelectedCustomer(null);
    }
  }, [calendarEvent.relatedItems?.calendarEvent, open]);

  // Kör sökning när debounced search term ändras
  useEffect(() => {
    if (debouncedSearchTerm.length >= 5) {
      searchCustomer({ CustomerEMaill: debouncedSearchTerm, CustomerName: debouncedSearchTerm, NewCustomerId: debouncedSearchTerm });
    }
  }, [debouncedSearchTerm, searchCustomer]);

  const handleCustomerSelect = (customer: CustomerModel) => {
    setSelectedCustomer(customer);
  };

  const handleProceedToAction = async () => {
    if (selectedCustomer) {
      const request: UpdateNextActionRequest = {
        title: calendarEvent.title,
        description: `Kopplat möte från kalender`,
        scheduledDate: format(
          calendarEvent.start,
          "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"
        ),
        externalId: calendarEvent.id,
        type:
          calendarEvent.relatedItems?.calendarEvent?.onlineMeeting !== null
            ? WorkItemTypeEnum.FollowUpDigitalMeeting
            : WorkItemTypeEnum.FollowUpPhysicalMeeting,
      };

      await updateNextAction({
        customerNo: selectedCustomer.CustomerNo,
        request,
      }).unwrap();
      onClose();
      setSelectedCustomer(null);
      setSearchTerm("");
    }
  };

  const formatEventInfo = () => {
    const startTime = new Date(calendarEvent.start).toLocaleString("sv-SE", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    return `${calendarEvent.title} - ${startTime}`;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={isUpdating ? undefined : onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Koppla möte till kund</DialogTitle>

        <DialogContent>
          {/* Event info */}
          <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Kalenderhändelse:
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {formatEventInfo()}
            </Typography>
            {calendarEvent.location && (
              <Typography variant="body2" color="text.secondary">
                📍 {calendarEvent.location}
              </Typography>
            )}
          </Box>

          {/* Customer search */}
          <TextField
            fullWidth
            label="Sök kund"
            placeholder="Skriv kundnamn eller organisationsnummer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Search results */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Något gick fel vid sökning av kunder. Försök igen senare.
            </Alert>
          )}

          {/* Update error */}
          {updateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Kunde inte skapa aktivitet. Försök igen senare.
            </Alert>
          )}

          {isFetching && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
              <Typography variant="body2" ml={1}>
                Söker kunder...
              </Typography>
            </Box>
          )}

          {!isFetching &&
            debouncedSearchTerm.length >= 2 &&
            customers.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                py={2}
              >
                Inga kunder hittades för "{debouncedSearchTerm}"
              </Typography>
            )}

          {customers.length > 0 && (
            <List sx={{ maxHeight: 300, overflow: "auto" }}>
              {customers.map((customer) => (
                <ListItem key={customer.CustomerNo} disablePadding>
                  <ListItemButton
                    onClick={() => handleCustomerSelect(customer)}
                    selected={
                      selectedCustomer?.CustomerNo === customer.CustomerNo
                    }
                    sx={{
                      border:
                        selectedCustomer?.CustomerNo === customer.CustomerNo
                          ? "2px solid"
                          : "1px solid transparent",
                      borderColor:
                        selectedCustomer?.CustomerNo === customer.CustomerNo
                          ? "primary.main"
                          : "transparent",
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <Avatar sx={{ mr: 2, bgcolor: "transparent" }}>
                      <PersonIcon color="secondary" />
                    </Avatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1" fontWeight="medium">
                            {customer.CustomerName}
                          </Typography>
                          {/* <Chip
                            label={
                              customer.CustomerType === CustomerTypeEnum.Normal
                                ? ""
                                : 
                            }
                            size="small"
                            color={
                              customer.CustomerType === CustomerTypeEnum.Normal
                                ? "primary"
                                : "secondary"
                            }
                            variant="outlined"
                          /> */}
                        </Box>
                      }
                      secondary={
                        <Box>
                          {customer.EntityNo && (
                            <Typography variant="caption" display="block">
                              Org.nr: {customer.EntityNo}
                            </Typography>
                          )}
                          {customer.Address && (
                            <Typography variant="caption" display="block">
                              {customer.Address}
                            </Typography>
                          )}
                          <Typography variant="caption" color="primary">
                            Kundnr: {customer.CustomerId}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}

          {searchTerm.length < 2 && (
            <Box textAlign="center" py={4}>
              <SearchIcon
                sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Skriv minst 6 tecken för att söka kunder
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={isUpdating}>
            Avbryt
          </Button>
          <Button
            variant="contained"
            onClick={handleProceedToAction}
            disabled={!selectedCustomer || isUpdating}
            startIcon={isUpdating ? <CircularProgress size={16} /> : undefined}
          >
            {isUpdating
              ? "Skapar aktivitet..."
              : `Skapa aktivitet för ${
                  selectedCustomer?.CustomerName || "kund"
                }`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CalendarCustomerSearchDialog;
